import { computed, ref } from 'vue'

import {
  useBooksyEppo,
  useLogger,
} from '@booksy-com/vue-tools'

import pkg from '@/../package.json'

import {
  EppoFeatureFlags,
  EppoExperiments,
  APP_DOMAIN,
} from './consts.js'

import { until } from '@vueuse/core'
import { useToast } from '@/composables/toast'

import { createEppoFallbackInstance } from './eppo-fallback.js'

let INSTANCE

const isReady = ref(false)
const logger = useLogger('use-eppo-composable')

// {Promise}
export const whenEppoIsReady = until(isReady).toBe(true)

export * from './consts.js'

export async function createEppo (store) {
  if (INSTANCE) {
    throw new Error('EppoInstance already created')
  }

  try {
    INSTANCE = await useBooksyEppo({
      app: pkg.name,
      app_version: pkg.version,
      app_domain: APP_DOMAIN,
      origin: process.env.VUE_APP_PROXY_URL
        || process.env.VUE_APP_API_URL
        || window.location.origin,
      country: computed(() => store.state.app.country),
      flags: {
        ...EppoFeatureFlags,
        ...EppoExperiments,
      },
      providers: {
        fingerprint: computed(() => store.state.app.fingerprint),
        business_id: computed(() => store.state.business?.business?.id),
      },
      clientConfig: {
        pollAfterSuccessfulInitialization: true,
      },
      commonAttributes: {
        business_primary_category: computed(() => store.state.business?.business?.primary_category_internal_name),
      },
    })

    INSTANCE.__ff = INSTANCE.ff

    INSTANCE.ff = (flagName, ...rest) => {
      try {
        return (window.eppoMock && flagName in window.eppoMock) ? window.eppoMock[flagName] : INSTANCE.__ff(flagName, ...rest)
      } catch (e) {
        logger.error(e)
      }
    }

    INSTANCE.experiment = (flagName) => {
      const flagValue = INSTANCE.ff(flagName)
      if (store.state.app.mode === 'development') {
        const toast = useToast()
        toast.show(`Experiment \`${flagName}\` checked. Returned value: ${flagValue}.`, {
          duration: 5000,
        })
      }
      return flagValue
    }

    return INSTANCE
  } catch (e) {
    logger.error(e)
  } finally {
    isReady.value = true
  }
}

export function useEppo () {
  if (!isReady.value || !INSTANCE) {
    logger.warn('BooksyEppo instance not created yet; return fallback with defaults value')
    return createEppoFallbackInstance()
  }

  return INSTANCE
}
